import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleAlert, PackageOpen, SearchX } from 'lucide-react';
import orderService from '../../services/orderService';
import { Pagination } from '../../ui';
import EmptyState from '../../components/site/EmptyState';
import { formatDate, formatINR } from '../../utils/format';
import { orderNumber, orderStateMeta, orderTitle, resolveOrderState } from './orderState';
import './style.css';

// yyyy-mm-dd from a date input -> the start or end of that day in the customer's time zone.
const dayBoundary = (value, end) => {
  if (!value) return undefined;
  const [y, m, d] = value.split('-').map(Number);
  const date = end ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d, 0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const SkeletonRows = ({ count = 4 }) => (
  <ul className="pk-orders__skeleton" aria-hidden="true">
    {Array.from({ length: count }, (_, i) => (
      <li key={i}>
        <span className="pk-skel" style={{ width: '22%' }} />
        <span className="pk-skel" style={{ width: '38%' }} />
        <span className="pk-skel" style={{ width: '14%' }} />
      </li>
    ))}
  </ul>
);

const OrderHistory = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Draft dates in the inputs vs the range actually applied to the list.
  const [draft, setDraft] = useState({ from: '', to: '' });
  const [range, setRange] = useState({ from: '', to: '' });
  const [state, setState] = useState({ status: 'loading', orders: [], total: 0, error: '' });

  const load = useCallback(async () => {
    setState(prev => ({ ...prev, status: 'loading', error: '' }));
    const result = await orderService.getMyOrders({
      currentPage: page,
      itemsPerPage: pageSize,
      dateStart: dayBoundary(range.from, false),
      dateEnd: dayBoundary(range.to, true),
    });
    if (!result.success) {
      setState({ status: 'error', orders: [], total: 0, error: result.error });
      return;
    }
    const data = result.data?.data || {};
    setState({
      status: 'ready',
      orders: Array.isArray(data.items) ? data.items : [],
      total: data.totalItems || 0,
      error: '',
    });
  }, [page, pageSize, range]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = Boolean(range.from || range.to);
  const draftChanged = draft.from !== range.from || draft.to !== range.to;

  const applyDates = e => {
    e.preventDefault();
    setPage(1);
    setRange({ ...draft });
  };

  const clearDates = () => {
    setDraft({ from: '', to: '' });
    setRange({ from: '', to: '' });
    setPage(1);
  };

  const renderBody = () => {
    if (state.status === 'loading') return <SkeletonRows count={Math.min(pageSize, 4)} />;

    if (state.status === 'error') {
      return (
        <EmptyState
          role="alert"
          icon={<CircleAlert />}
          title="We couldn't load your orders"
          text={state.error}
          actions={
            <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={load}>
              Retry
            </button>
          }
        />
      );
    }

    if (!state.orders.length) {
      return filtered ? (
        <EmptyState
          icon={<SearchX />}
          title="No orders in these dates"
          text="Try a wider date range, or clear the dates to see every order."
          actions={
            <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={clearDates}>
              Clear dates
            </button>
          }
        />
      ) : (
        <EmptyState
          icon={<PackageOpen />}
          title="No orders yet"
          text="Projects you buy show up here, with their files and repository access."
          actions={
            <Link to="/products" className="pk-btn pk-btn--primary pk-btn--sm">
              Browse projects
            </Link>
          }
        />
      );
    }

    return (
      <table className="pk-orders__table">
        <caption className="pk-account__sr">Your orders</caption>
        <thead>
          <tr>
            <th scope="col">Order</th>
            <th scope="col">Project</th>
            <th scope="col">Date</th>
            <th scope="col" className="is-num">
              Amount
            </th>
            <th scope="col">Status</th>
            <th scope="col">
              <span className="pk-account__sr">Details</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {state.orders.map(order => {
            const meta = orderStateMeta(resolveOrderState(order));
            const number = orderNumber(order);
            return (
              <tr key={order.id || number}>
                <td data-label="Order" className="pk-orders__number">
                  {number}
                </td>
                <td data-label="Project" className="pk-orders__project">
                  {orderTitle(order)}
                </td>
                <td data-label="Date" className="pk-orders__date">
                  {formatDate(order.createdAt)}
                </td>
                <td data-label="Amount" className="is-num pk-orders__amount">
                  {formatINR(order.totalAmount)}
                </td>
                <td data-label="Status" className="pk-orders__status">
                  <span className="pk-badge" data-tone={meta.tone}>
                    {meta.label}
                  </span>
                </td>
                <td className="pk-orders__action">
                  <Link
                    to={`/profile/order-details/${order.id}`}
                    className="pk-btn pk-btn--secondary pk-btn--xs"
                    aria-label={`View order ${number}`}
                  >
                    View
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <section className="pk-orders" aria-labelledby="orders-title">
      <div className="pk-orders__head">
        <h2 className="pk-title-2" id="orders-title">
          Orders
        </h2>
        <form className="pk-orders__filter" onSubmit={applyDates} aria-label="Filter orders by date">
          <div className="pk-field">
            <label className="pk-label" htmlFor="orders-from">
              From
            </label>
            <input
              id="orders-from"
              type="date"
              className="pk-input"
              value={draft.from}
              max={draft.to || undefined}
              onChange={e => setDraft(d => ({ ...d, from: e.target.value }))}
            />
          </div>
          <div className="pk-field">
            <label className="pk-label" htmlFor="orders-to">
              To
            </label>
            <input
              id="orders-to"
              type="date"
              className="pk-input"
              value={draft.to}
              min={draft.from || undefined}
              onChange={e => setDraft(d => ({ ...d, to: e.target.value }))}
            />
          </div>
          <div className="pk-orders__filter-actions">
            <button type="submit" className="pk-btn pk-btn--primary pk-btn--sm" disabled={!draftChanged}>
              Apply
            </button>
            {(filtered || draft.from || draft.to) && (
              <button type="button" className="pk-btn pk-btn--ghost pk-btn--sm" onClick={clearDates}>
                Clear
              </button>
            )}
          </div>
        </form>
      </div>

      <div aria-live="polite" aria-busy={state.status === 'loading'}>
        {renderBody()}
      </div>

      {state.status === 'ready' && state.total > 10 && (
        <Pagination
          className="pk-orders__pagination"
          current={page}
          total={state.total}
          pageSize={pageSize}
          pageSizeOptions={[10, 20, 50]}
          showSizeChanger
          showTotal={(total, [from, to]) => `${from}–${to} of ${total} orders`}
          onChange={(nextPage, nextSize) => {
            if (nextSize !== pageSize) {
              setPageSize(nextSize);
              setPage(1);
            } else {
              setPage(nextPage);
            }
          }}
        />
      )}
    </section>
  );
};

export default OrderHistory;
